import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../App.css";
import { supabase } from "../supabaseClient";
import { LicenseIcon } from "../assets/icons/icons";
import { getCategoriesForIndustry } from "../constants/licenseCategories";
import PricingSection from "../components/PricingSection";
import { trackGoal } from "../utils/datafast";
import { sortLicensesByState } from "../utils/licenses";
import useFocusTrap from "../hooks/useFocusTrap";

const INDUSTRIES = [
  "Insurance",
  "Real Estate",
  "Mortgage",
  "Financial Services",
  "Legal",
  "Healthcare",
  "Education",
  "Construction",
  "Other",
];

const DEFAULT_INDUSTRY = INDUSTRIES[0];
const TRACKED_VERIFIED_KEY = "datafast_verified_license_ids";

const getCategoryOptions = (industry, currentCategory) => {
  const base = getCategoriesForIndustry(industry);
  if (currentCategory && !base.includes(currentCategory)) {
    return [currentCategory, ...base];
  }
  return base;
};

const isPdfFile = (file) => {
  if (!file) return false;
  const name = String(file.name || "").toLowerCase();
  const type = String(file.type || "").toLowerCase();
  return type === "application/pdf" || name.endsWith(".pdf");
};

const normalizeStoragePath = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const cleaned = raw.replace(/^\/+/, "");
  const withoutBucket = cleaned.replace(/^license_docs\//i, "");
  if (withoutBucket !== cleaned) return withoutBucket;

  const storagePattern = /storage\/v1\/object\/(?:public|sign|auth)?\/?license_docs\/(.+)/i;

  if (/^https?:\/\//i.test(raw)) {
    try {
      const url = new URL(raw);
      const match = url.pathname.match(storagePattern);
      return match ? match[1] : "";
    } catch {
      return "";
    }
  }

  const match = cleaned.match(storagePattern);
  return match ? match[1] : cleaned;
};

const readTrackedVerifiedIds = () => {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(TRACKED_VERIFIED_KEY);
    const parsed = JSON.parse(raw || "[]");
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.map((id) => String(id)));
  } catch {
    return new Set();
  }
};

const writeTrackedVerifiedIds = (ids) => {
  if (typeof window === "undefined") return;
  try {
    const list = Array.from(ids).slice(-200);
    window.localStorage.setItem(TRACKED_VERIFIED_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
};

export default function LicensesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removeTargetId, setRemoveTargetId] = useState(null);
  const [showRemoveAlert, setShowRemoveAlert] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState("free");
  const [subscriptionStatus, setSubscriptionStatus] = useState("inactive");
  const [totalLicensesCount, setTotalLicensesCount] = useState(0);
  const [checkoutError, setCheckoutError] = useState("");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [justSubmittedFirst, setJustSubmittedFirst] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [form, setForm] = useState({
    industry: DEFAULT_INDUSTRY,
    state: "FL",
    category: getCategoriesForIndustry(DEFAULT_INDUSTRY)[0] || "",
    licenseNumber: "",
    issuedOn: "",
    expiresOn: "",
    document: null,
    supportingDocument: null,
    notes: "",
  });
  const [removeDocument, setRemoveDocument] = useState(false);
  const removeAlertTimerRef = useRef(null);
  const autoOpenHandledRef = useRef(false);
  const editModalRef = useRef(null);
  const removeConfirmModalRef = useRef(null);
  const upgradeModalRef = useRef(null);
  const successModalRef = useRef(null);
  const pricingModalRef = useRef(null);

  const isPaid = useMemo(() => {
    const tier = String(subscriptionTier || "").toLowerCase();
    const status = String(subscriptionStatus || "").toLowerCase();
    if (tier === "pro" || tier === "elite") return true;
    if (status === "pro" || status === "elite" || status === "active") return true;
    return false;
  }, [subscriptionTier, subscriptionStatus]);

  const reachedFreeLimit = !isPaid && totalLicensesCount >= 1;

  const resetFormState = () => {
    setForm({
      industry: DEFAULT_INDUSTRY,
      state: "FL",
      category: getCategoriesForIndustry(DEFAULT_INDUSTRY)[0] || "",
      licenseNumber: "",
      issuedOn: "",
      expiresOn: "",
      document: null,
      supportingDocument: null,
      notes: "",
    });
    setRemoveDocument(false);
    setEditingId(null);
  };

  const populateFormFromLicense = (lic) => {
    const fallbackCategory = getCategoriesForIndustry(lic.industry || DEFAULT_INDUSTRY)[0] || "";
    setForm({
      industry: lic.industry || DEFAULT_INDUSTRY,
      state: lic.state || "FL",
      category: lic.category || fallbackCategory,
      licenseNumber: lic.license_number || lic.licenseNumber || "",
      issuedOn: lic.issued_on || lic.issuedOn || "",
      expiresOn: lic.expires_on || lic.expiresOn || "",
      document: null,
      supportingDocument: lic.supporting_document || lic.supportingDocument || null,
      notes: lic.description || "",
    });
    setRemoveDocument(false);
  };

  useEffect(() => {
    const loadLicenses = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) return;

      const [{ data: profileRow }, { count: licenseCount }] = await Promise.all([
        supabase
          .from("profiles")
          .select("subscription_tier,subscription_status")
          .eq("id", userId)
          .maybeSingle(),
        supabase
          .from("licenses")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId)
          .eq("removed", false),
      ]);

      if (profileRow) {
        setSubscriptionTier(profileRow.subscription_tier || "free");
        setSubscriptionStatus(profileRow.subscription_status || "inactive");
      }
      if (typeof licenseCount === "number") {
        setTotalLicensesCount(licenseCount);
      }

      const { data, error } = await supabase
        .from("licenses")
        .select("id,industry,state,category,license_number,status,issued_on,expires_on,verified,supporting_document,description")
        .eq("user_id", userId)
        .eq("verified", true)
        .eq("removed", false)
        .order("state", { ascending: true })
        .order("industry", { ascending: true })
        .order("category", { ascending: true });

      if (!error && data) {
        setLicenses(
          sortLicensesByState((data || []).map((lic) => ({
            id: lic.id,
            industry: lic.industry,
            state: lic.state,
            category: lic.category,
            licenseNumber: lic.license_number,
            status: lic.status || "Pending",
            issuedOn: lic.issued_on,
            expiresOn: lic.expires_on,
            supportingDocument: lic.supporting_document,
            description: lic.description,
            expiringSoon:
              lic.expires_on && new Date(lic.expires_on) - new Date() > 0 && (new Date(lic.expires_on) - new Date()) / (1000 * 60 * 60 * 24) <= 60,
          })))
        );
      }

      // If user came from Verification page with an edit request, fetch that license (even if not verified)
      const pendingEditId = sessionStorage.getItem("editLicenseId");
      if (pendingEditId && userId) {
        const { data: editLic } = await supabase
          .from("licenses")
          .select("id,industry,state,category,license_number,status,issued_on,expires_on,verified,supporting_document,description")
          .eq("id", pendingEditId)
          .eq("user_id", userId)
          .eq("removed", false)
          .maybeSingle();
        sessionStorage.removeItem("editLicenseId");
        if (editLic) {
          setEditingId(editLic.id);
          populateFormFromLicense(editLic);
          setShowModal(true);
        }
      }
    };

    loadLicenses();
  }, []);

  useEffect(() => {
    if (!licenses.length) return;
    const tracked = readTrackedVerifiedIds();
    let updated = false;
    const next = new Set(tracked);
    licenses.forEach((lic) => {
      const id = lic?.id;
      if (!id) return;
      const idStr = String(id);
      if (tracked.has(idStr)) return;
      trackGoal("license_verified", {
        license_type: lic?.category || lic?.industry || "",
        state: lic?.state || "",
      });
      next.add(idStr);
      updated = true;
    });
    if (updated) writeTrackedVerifiedIds(next);
  }, [licenses]);

  useEffect(() => {
    if (autoOpenHandledRef.current) return;
    const params = new URLSearchParams(location.search);
    const shouldOpen = params.get("add") === "1" || params.get("source") === "onboarding";
    if (!shouldOpen) return;
    if (totalLicensesCount === 0 && !reachedFreeLimit) {
      setEditingId(null);
      setShowModal(true);
    }
    autoOpenHandledRef.current = true;
    params.delete("add");
    params.delete("source");
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate, totalLicensesCount, reachedFreeLimit]);

  useEffect(() => {
    return () => {
      if (removeAlertTimerRef.current) {
        clearTimeout(removeAlertTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showRemoveAlert) return;
    if (removeAlertTimerRef.current) clearTimeout(removeAlertTimerRef.current);
    removeAlertTimerRef.current = setTimeout(() => {
      setShowRemoveAlert(false);
      removeAlertTimerRef.current = null;
    }, 5000);
  }, [showRemoveAlert]);

  const dismissRemoveAlert = () => {
    if (removeAlertTimerRef.current) {
      clearTimeout(removeAlertTimerRef.current);
      removeAlertTimerRef.current = null;
    }
    setShowRemoveAlert(false);
  };

  const openRemoveConfirm = () => {
    if (!editingId) return;
    setRemoveTargetId(editingId);
    setShowRemoveConfirm(true);
  };

  const closeRemoveConfirm = () => {
    setShowRemoveConfirm(false);
    setRemoveTargetId(null);
  };

  const handleAddLicenseClick = () => {
    if (reachedFreeLimit) {
      setCheckoutError("");
      setShowUpgradeModal(true);
      return;
    }
    setEditingId(null);
    setShowModal(true);
  };

  const handleRemoveLicense = async () => {
    const targetId = removeTargetId || editingId;
    if (!targetId) return;
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) return;

    const { error } = await supabase
      .from("licenses")
      .update({ removed: true })
      .eq("id", targetId)
      .eq("user_id", userId);

    if (error) {
      console.error("Error removing license:", error.message);
      alert("Unable to remove license. Please try again.");
      return;
    }

    setLicenses((prev) => sortLicensesByState(prev.filter((lic) => lic.id !== targetId)));
    setTotalLicensesCount((prev) => Math.max(0, prev - 1));
    closeRemoveConfirm();
    setShowModal(false);
    resetFormState();
    setShowRemoveAlert(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) return;
    if (!editingId && reachedFreeLimit) {
      setCheckoutError("");
      setShowUpgradeModal(true);
      return;
    }
    const isFirstSubmission = !editingId && totalLicensesCount === 0;

    if (!form.document && (!form.supportingDocument || removeDocument)) {
      alert("Please upload a PDF of your license.");
      return;
    }
    if (form.document && !isPdfFile(form.document)) {
      alert("Please upload a PDF file.");
      return;
    }

    let supportingDocPath = removeDocument ? null : form.supportingDocument || null;

    if (form.document) {
      const ext = form.document.name.split(".").pop();
      const filePath = `${userId}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("license_docs")
        .upload(filePath, form.document, { upsert: true });
      if (uploadError) {
        console.error("Error uploading document:", uploadError.message);
      } else {
        supportingDocPath = filePath;
      }
    }

    const payload = {
      user_id: userId,
      industry: form.industry,
      state: form.state,
      category: form.category,
      license_number: form.licenseNumber,
      issued_on: form.issuedOn || null,
      expires_on: form.expiresOn || null,
      supporting_document: supportingDocPath || null,
      description: form.notes,
      status: "pending",
      verified: false,
    };

    let opError = null;
    if (editingId) {
      const { error } = await supabase
        .from("licenses")
        .update(payload)
        .eq("id", editingId)
        .eq("user_id", userId);
      opError = error;
    } else {
      const { error } = await supabase.from("licenses").insert([payload]);
      opError = error;
    }

    if (opError) {
      console.error("Error saving license:", opError.message);
    } else {
      const { data, error: reloadError } = await supabase
        .from("licenses")
        .select("id,industry,state,category,license_number,status,issued_on,expires_on,verified,supporting_document,description")
        .eq("user_id", userId)
        .eq("verified", true)
        .eq("removed", false)
        .order("state", { ascending: true })
        .order("industry", { ascending: true })
        .order("category", { ascending: true });

      if (!reloadError && data) {
        setLicenses(
          sortLicensesByState((data || []).map((lic) => ({
            id: lic.id,
            industry: lic.industry,
            state: lic.state,
            category: lic.category,
            licenseNumber: lic.license_number,
            status: lic.status || "Pending",
            issuedOn: lic.issued_on,
            expiresOn: lic.expires_on,
            supportingDocument: lic.supporting_document,
            description: lic.description,
          })))
        );
      }
      setShowModal(false);
      resetFormState();
      if (!editingId) {
        setTotalLicensesCount((prev) => prev + 1);
        setJustSubmittedFirst(isFirstSubmission);
        trackGoal("license_added", {
          license_type: form.category || form.industry || "",
          state: form.state || "",
        });
      }
      setShowSuccessModal(true);
    }
  };

  const handleViewLicense = async (lic) => {
    const docPath = lic.supportingDocument;
    if (!docPath) {
      alert("No document on file.");
      return;
    }
    if (/^https?:\/\//i.test(docPath)) {
      window.open(docPath, "_blank", "noopener,noreferrer");
      return;
    }
    const storagePath = normalizeStoragePath(docPath);
    if (!storagePath) {
      alert("No document on file.");
      return;
    }
    const { data: signedDoc, error: signErr } = await supabase.storage
      .from("license_docs")
      .createSignedUrl(storagePath, 3600);
    if (signErr || !signedDoc?.signedUrl) {
      alert("Unable to open license document. Please try again.");
      return;
    }
    window.open(signedDoc.signedUrl, "_blank", "noopener,noreferrer");
  };

  useFocusTrap({
    isOpen: showModal,
    containerRef: editModalRef,
    onClose: () => setShowModal(false),
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: showRemoveConfirm,
    containerRef: removeConfirmModalRef,
    onClose: closeRemoveConfirm,
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: showUpgradeModal,
    containerRef: upgradeModalRef,
    onClose: () => setShowUpgradeModal(false),
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: showSuccessModal,
    containerRef: successModalRef,
    onClose: () => {
      setShowSuccessModal(false);
      setJustSubmittedFirst(false);
    },
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: showPricingModal,
    containerRef: pricingModalRef,
    onClose: () => setShowPricingModal(false),
    lockScroll: true,
  });

  return (
    <>
      <div className="page">
        <div className="page-header">
          <div>
            <p className="eyebrow">Manage your licenses</p>
            <h1 data-page-heading="true">Licenses</h1>
          </div>
          {licenses.length > 0 && (
            <button className="primary dark" onClick={handleAddLicenseClick}>Add License</button>
          )}
        </div>
        {showRemoveAlert && (
          <div className="alert success">
            <span>License removed successfully.</span>
            <button type="button" className="alert-close" onClick={dismissRemoveAlert} aria-label="Dismiss">
              x
            </button>
          </div>
        )}
        {reachedFreeLimit && (
          <div className="alert info">
            <span>You're set up! Finish verifying your licenses now.</span>
            <div className="alert-actions">
              <button
                className="ghost"
                type="button"
                onClick={() => {
                  trackGoal("upsell_interacted", {
                    upsell_id: "licenses_free_limit_alert",
                    action: "verify_more",
                  });
                  setShowPricingModal(true);
                }}
                data-fast-goal="view_pricing"
                data-fast-goal-source="licenses"
              >
                Verify more licenses
              </button>
            </div>
          </div>
        )}
        {checkoutError && <div className="error-message">{checkoutError}</div>}

        <div className="license-list">
          {licenses.length === 0 && (
            <div className="panel" style={{ textAlign: "center" }}>
              <h4>No active licenses yet</h4>
              {!reachedFreeLimit && (
                <>
                  <p className="muted">Add a license to start the verification process.</p>
                  <div className="button-row" style={{ justifyContent: "center", marginTop: 10 }}>
                    <button className="primary dark" onClick={handleAddLicenseClick}>
                      Add License
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {licenses.map((lic) => (
            <div
              key={lic.id}
              className="license-card"
            >
              <div className="license-top">
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <span className="card-line-icon">
                      <LicenseIcon className="card-line-icon" />
                    </span>
                    <p className="pill">{lic.industry}</p>
                  </div>
                  <h4>{lic.category || "Category not set"}</h4>
                  <p className="muted">License #{lic.licenseNumber || "-"}</p>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                  <span className={`badge ${lic.status === "Active" || lic.status === "active" ? "success" : "muted"}`}>
                    {lic.status}
                  </span>
                  {lic.expiringSoon && (
                    <span className="badge amber">Expiring soon</span>
                  )}
                </div>
              </div>
              <div className="license-meta">
                <span>State: {lic.state}</span>
                <span>Issued: {lic.issuedOn || "-"}</span>
                <span>Expires: {lic.expiresOn || "-"}</span>
                <div className="license-actions">
                  <button
                    className="ghost"
                    type="button"
                    onClick={() => handleViewLicense(lic)}
                  >
                    View License
                  </button>
                  <button
                    className="ghost"
                    type="button"
                    onClick={() => {
                      setEditingId(lic.id);
                      populateFormFromLicense(lic);
                      setShowModal(true);
                    }}
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="modal-backdrop" onClick={() => setShowModal(false)}>
            <div
              ref={editModalRef}
              className="modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="licenses-edit-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="licenses-edit-modal-title">{editingId ? "Edit License" : "Add License"}</h3>
                <button className="icon-btn" type="button" aria-label="Close license form dialog" onClick={() => { setShowModal(false); setEditingId(null); }}>x</button>
              </div>
              <form className="form-grid" onSubmit={handleSubmit}>
                <div className="form-row">
                  <label htmlFor="licenses-industry">Industry *</label>
                  <select
                    id="licenses-industry"
                    value={form.industry}
                    onChange={(e) => {
                      const nextIndustry = e.target.value;
                      const nextOptions = getCategoriesForIndustry(nextIndustry);
                      const nextCategory = nextOptions.includes(form.category)
                        ? form.category
                        : (nextOptions[0] || "");
                      setForm({ ...form, industry: nextIndustry, category: nextCategory });
                    }}
                    required
                  >
                    {INDUSTRIES.map((ind) => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label htmlFor="licenses-state">State *</label>
                  <select
                    id="licenses-state"
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    required
                  >
                    {["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"].map((st) => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label htmlFor="licenses-category">Category</label>
                  <select
                    id="licenses-category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                  >
                    {getCategoryOptions(form.industry, form.category).map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label htmlFor="licenses-license-number">License Number *</label>
                  <input
                    id="licenses-license-number"
                    type="text"
                    placeholder="e.g. A12345"
                    value={form.licenseNumber}
                    onChange={(e) => setForm({ ...form, licenseNumber: e.target.value })}
                    required
                  />
                </div>
                <div className="form-row dual">
                  <div>
                    <label htmlFor="licenses-issued-on">Issued On</label>
                    <input
                      id="licenses-issued-on"
                      type="date"
                      value={form.issuedOn}
                      onChange={(e) => setForm({ ...form, issuedOn: e.target.value })}
                    />
                  </div>
                  <div>
                    <label htmlFor="licenses-expires-on">Expires On</label>
                    <input
                      id="licenses-expires-on"
                      type="date"
                      value={form.expiresOn}
                      onChange={(e) => setForm({ ...form, expiresOn: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <label htmlFor="licenses-supporting-document">Supporting Document (PDF) *</label>
                  <input
                    id="licenses-supporting-document"
                    type="file"
                    accept="application/pdf"
                    required={!form.supportingDocument || removeDocument}
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      if (file && !isPdfFile(file)) {
                        alert("Please upload a PDF file.");
                        e.target.value = "";
                        setForm({ ...form, document: null });
                        return;
                      }
                      setForm({ ...form, document: file });
                    }}
                  />
                  {form.supportingDocument && !removeDocument && (
                    <div className="muted" style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
                      <span>Current file:</span>
                      <span style={{ fontWeight: 700 }}>{form.supportingDocument}</span>
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => {
                          setForm({ ...form, supportingDocument: null, document: null });
                          setRemoveDocument(true);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <label htmlFor="licenses-notes">Notes</label>
                  <textarea
                    id="licenses-notes"
                    rows="4"
                    placeholder="Additional notes..."
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </div>
                <div className="button-row sticky">
                  <button type="submit" className="primary dark">Save License</button>
                  {editingId ? (
                    <button type="button" className="danger" onClick={openRemoveConfirm}>
                      Remove License
                    </button>
                  ) : (
                    <button type="button" className="ghost" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      {showRemoveConfirm && (
        <div className="modal-backdrop" onClick={closeRemoveConfirm}>
          <div
            ref={removeConfirmModalRef}
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="licenses-remove-modal-title"
            style={{ maxWidth: 420 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="licenses-remove-modal-title">Remove license?</h3>
            </div>
            <p className="muted">This license will be removed from your profile and search results.</p>
            <div className="button-row" style={{ marginTop: 12 }}>
              <button className="ghost" type="button" onClick={closeRemoveConfirm}>
                Cancel
              </button>
              <button className="danger" type="button" onClick={handleRemoveLicense}>
                Remove my license
              </button>
            </div>
          </div>
        </div>
      )}
      {showUpgradeModal && (
        <div className="modal-backdrop" onClick={() => setShowUpgradeModal(false)}>
          <div
            ref={upgradeModalRef}
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="licenses-upgrade-modal-title"
            style={{ maxWidth: 440 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="licenses-upgrade-modal-title">Upgrade to add more licenses</h3>
            </div>
            <p className="muted">
              Your first license is free. Upgrade to Pro or Elite to add unlimited licenses.
            </p>
            {checkoutError && <p className="error-message">{checkoutError}</p>}
            <div className="button-row" style={{ marginTop: 12 }}>
              <button className="ghost" type="button" onClick={() => setShowUpgradeModal(false)}>
                Not now
              </button>
              <button
                className="primary dark"
                type="button"
                onClick={() => {
                  trackGoal("upsell_interacted", {
                    upsell_id: "licenses_upgrade_modal",
                    action: "unlock_more",
                  });
                  setShowUpgradeModal(false);
                  setShowPricingModal(true);
                }}
                data-fast-goal="view_pricing"
                data-fast-goal-source="licenses"
              >
                Unlock More licenses
              </button>
            </div>
          </div>
        </div>
      )}
      {showSuccessModal && (
        <div
          className="modal-backdrop"
          onClick={() => {
            setShowSuccessModal(false);
            setJustSubmittedFirst(false);
          }}
        >
          <div
            ref={successModalRef}
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="licenses-success-modal-title"
            style={{ maxWidth: 420 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="licenses-success-modal-title">Congratulations</h3>
              <button
                className="icon-btn"
                type="button"
                onClick={() => {
                  setShowSuccessModal(false);
                  setJustSubmittedFirst(false);
                }}
                aria-label="Close"
              >
                x
              </button>
            </div>
            <p className="muted">Your license was submitted successfully.</p>
            {justSubmittedFirst && !isPaid && (
              <div className="upgrade-callout" style={{ marginTop: 12 }}>
                <p className="muted upgrade-callout-subtext">
                  Take your visibility to the next level by adding more licenses and upgrading today.
                </p>
                <div className="button-row" style={{ justifyContent: "flex-end" }}>
                  <button
                    className="primary dark"
                    type="button"
                    onClick={() => {
                      trackGoal("upsell_interacted", {
                        upsell_id: "licenses_post_submit_callout",
                        action: "add_more",
                      });
                      setShowSuccessModal(false);
                      setJustSubmittedFirst(false);
                      setShowUpgradeModal(true);
                    }}
                  >
                    Add More Licenses
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {showPricingModal && (
        <div className="modal-backdrop" style={{ zIndex: 210 }} onClick={() => setShowPricingModal(false)}>
          <div
            ref={pricingModalRef}
            className="modal pricing-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="licenses-pricing-modal-title"
            style={{ maxWidth: 1200, maxHeight: "80vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pricing-modal-card">
              <h3 id="licenses-pricing-modal-title" className="sr-only">Pricing plans</h3>
              <button
                className="icon-btn pricing-modal-close"
                type="button"
                onClick={() => setShowPricingModal(false)}
                aria-label="Close"
              >
                x
              </button>
              <PricingSection
                id="pricing-modal-licenses"
                disableFreeCta
                freeCtaLabel="Current Plan"
                currentTier={subscriptionTier}
                currentStatus={subscriptionStatus}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}


